import { X } from "lucide-react";
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { NavLink, useLocation, Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useLogo } from "@/hooks/useLogo";
import { useSidebarNavigation } from "@/hooks/useSidebarNavigation";
import { Skeleton } from "@/components/ui/skeleton";
import { useFeatureFlags } from "@/contexts/FeatureFlagsContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { ProfileList, type Profile } from "@/components/auth/ProfileList";
import { clearRolesCache } from "@/lib/rolesCache";
import { clearGroupsCache } from "@/hooks/useGroupChildren";
import { clearUserInfoCache } from "@/hooks/useUserInfo";
import { clearUnitsCache } from "@/lib/unitsCache";
import { clearDigitalAssetsCache } from "@/lib/digitalAssetsCache";
import { clearMessageBoardCache } from "@/lib/messageBoardCache";

import { clearUnitDetailCache } from "@/lib/unitDetailCache";
import { useQueryClient } from '@tanstack/react-query';
export function AppSidebar() {
  const { state } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const { quickLinks, isLoading: isLoadingNav } = useSidebarNavigation();
  const { logoUrl } = useLogo();
  const { isFeatureEnabled } = useFeatureFlags();
  const { user, setProfileToken } = useAuth();
  const useDWProfiles = isFeatureEnabled('useDWProfiles');

  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);

  const getStoredProfile = () => {
    try {
      const stored = sessionStorage.getItem('selectedProfile');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  };

  const [selectedProfile, setSelectedProfile] = useState(getStoredProfile);

  useEffect(() => {
    setSelectedProfile(getStoredProfile());
  }, [isPanelOpen]);

  // Load profiles from cache when panel opens; fetch only if not cached
  useEffect(() => {
    if (!isPanelOpen || !user?.loginToken) return;

    const cached = sessionStorage.getItem('userProfiles');
    if (cached) {
      try {
        setProfiles(JSON.parse(cached));
        setIsLoadingProfiles(false);
        return;
      } catch {}
    }

    let cancelled = false;
    setIsLoadingProfiles(true);
    supabase.functions.invoke('get-user-profiles', {
      body: { token: user.loginToken },
    }).then(({ data, error }) => {
      if (cancelled) return;
      if (data?.profiles) {
        const p = Array.isArray(data.profiles) ? data.profiles : [];
        sessionStorage.setItem('userProfiles', JSON.stringify(p));
        setProfiles(p);
      }
      setIsLoadingProfiles(false);
    });
    return () => { cancelled = true; };
  }, [isPanelOpen, user?.loginToken]);

  const profileName = selectedProfile?.company
    ? `${selectedProfile.company} #${selectedProfile.customerNumber}`
    : 'Camping World #134';
  const profileLocation = selectedProfile?.city
    ? `${selectedProfile.city}, ${selectedProfile.state || ''}`
    : 'Statesville, NC';

  const handleProfileSelect = async (profile: Profile) => {
    // Clear all caches before switching profile
    clearGroupsCache();
    clearUserInfoCache();
    clearUnitsCache();
    clearRolesCache();
    clearDigitalAssetsCache();
    clearMessageBoardCache();
    
    clearUnitDetailCache();
    queryClient.invalidateQueries({ queryKey: ['get-favorites-lists'] });
    queryClient.invalidateQueries({ queryKey: ['get-favorites-list-detail'] });
    queryClient.invalidateQueries({ queryKey: ['favorites-all-items'] });
    sessionStorage.removeItem('userProfiles');

    sessionStorage.setItem('selectedProfile', JSON.stringify({
      customerNumber: profile.customerNumber,
      company: profile.company,
      id: profile.id,
      address: profile.address,
      address2: profile.address2,
      city: profile.city,
      state: profile.state,
      zip: profile.zip,
      country: profile.country,
      countryCode: profile.countryCode,
    }));

    // Switch profile to get a new profileToken
    if (user?.loginToken && profile.id) {
      try {
        const { data, error } = await supabase.functions.invoke('switch-profile', {
          body: { token: user.loginToken, profileUserId: profile.id }
        });
        if (!error && data?.token) {
          setProfileToken(data.token);
        }
      } catch (err) {
        console.error('Error switching profile:', err);
      }
    }

    setSelectedProfile({
      customerNumber: profile.customerNumber,
      company: profile.company,
      id: profile.id,
      city: profile.city,
      state: profile.state,
    });
    setIsPanelOpen(false);
    navigate('/', { replace: true });
    window.location.reload();
  };

  useEffect(() => {
    const handler = () => setIsPanelOpen(true);
    window.addEventListener('open-dealership-panel', handler);
    return () => window.removeEventListener('open-dealership-panel', handler);
  }, []);

  const currentCustomerNumber = selectedProfile?.customerNumber;

  const dealershipContent = (
    <div className="space-y-4">
      <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg">
        <div className="font-medium text-sm text-foreground">Current Dealership</div>
        <div className="text-sm text-primary font-medium mt-1">{profileName}</div>
        <div className="text-xs text-muted-foreground">{profileLocation}</div>
      </div>
      <div className="space-y-2">
        
        {isLoadingProfiles ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ) : (
          <ProfileList
            profiles={profiles}
            onSelect={handleProfileSelect}
            currentCustomerNumber={currentCustomerNumber}
            variant="light"
          />
        )}
      </div>
    </div>
  );

  return (
    <>
      <Sidebar className="border-r border-sidebar-border bg-sidebar shadow-sm" collapsible="icon">
        <SidebarHeader className={`border-b border-sidebar-border bg-[hsl(var(--sidebar-header-background))] relative ${state === "collapsed" ? "px-2 py-3" : "p-6"}`}>
          <SidebarTrigger className={`absolute top-4 p-2 hover:bg-white/10 hover:text-white rounded-md transition-colors z-10 ${state === "collapsed" ? "left-1/2 transform -translate-x-1/2" : "right-4"}`} />
          <div className="flex flex-col items-center space-y-3">
            {state !== "collapsed" && (
              <Link to="/" className="hover:opacity-80 transition-opacity">
                <img src={logoUrl} alt="Logo" className="w-28 h-28 object-contain" />
              </Link>
            )}
            {state !== "collapsed" && <div className="flex flex-col items-center text-center">
              <span className="font-bold text-xl text-sidebar-foreground leading-tight">
                DEALER Portal
              </span>
              <button
                onClick={() => setIsPanelOpen(true)}
                className="mt-2 text-center p-2 rounded-md hover:bg-white/10 transition-colors duration-200 group"
              >
                <div className="text-sm text-sidebar-foreground/80 font-medium group-hover:text-sidebar-foreground">
                  {profileName}
                </div>
                <div className="text-xs text-sidebar-foreground/60 group-hover:text-sidebar-foreground/80">
                  {profileLocation}
                </div>
              </button>
            </div>}
          </div>
        </SidebarHeader>

        <SidebarContent className={`bg-[hsl(var(--sidebar-header-background))] ${state === "collapsed" ? "px-[5px] py-3" : "px-[5px] py-3"}`}>
          <SidebarGroup className={state === "collapsed" ? "px-0" : ""}>
            <SidebarGroupLabel className="text-xs font-semibold text-sidebar-foreground/50 uppercase tracking-wider px-3 py-3 mb-2">
              {state !== "collapsed" ? "Quick Links" : ""}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className="space-y-1">
                {isLoadingNav ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <SidebarMenuItem key={i}>
                      <Skeleton className={`rounded-lg ${state === "collapsed" ? "h-10 w-10 mx-auto" : "h-8 w-full"}`} />
                    </SidebarMenuItem>
                  ))
                ) : quickLinks.map((item, index) => {
                  const isActive = location.pathname === item.url;
                  const Icon = item.icon;
                  return (
                    <SidebarMenuItem key={item.title} className={state === "collapsed" ? "flex justify-center" : ""}>
                      <SidebarMenuButton asChild className="hover:bg-transparent hover:text-current">
                        {item.url === "#" ? (
                          <a href={item.url} className={`group relative overflow-hidden flex items-center rounded-lg text-sm font-medium text-white hover:text-white hover:bg-white/10 transition-all duration-200 ${state === "collapsed" ? "justify-center w-10 h-10 mx-auto p-0" : "gap-3 px-3 py-2"}`} style={{ animationDelay: `${index * 50}ms` }}>
                            <div className={`flex items-center z-10 relative ${state === "collapsed" ? "" : "gap-3"}`}>
                              <Icon className="w-5 h-5 transition-transform duration-200 group-hover:scale-110" />
                              {state !== "collapsed" && <span className="font-medium">{item.title}</span>}
                            </div>
                            <div className="absolute inset-0 bg-white/5 rounded-lg transform scale-0 group-hover:scale-100 transition-transform duration-200 ease-out" />
                          </a>
                        ) : (
                          <NavLink to={item.url} className={`group relative overflow-hidden flex items-center rounded-lg text-sm font-medium transition-all duration-200 ${state === "collapsed" ? "justify-center w-10 h-10 mx-auto p-0" : "gap-3 px-3 py-2"} ${isActive ? "text-white bg-white/20" : "text-white hover:text-white hover:bg-white/10"}`} style={{ animationDelay: `${index * 50}ms` }}>
                            <div className={`flex items-center z-10 relative ${state === "collapsed" ? "" : "gap-3"}`}>
                              <Icon className="w-5 h-5 transition-transform duration-200 group-hover:scale-110" />
                              {state !== "collapsed" && <span className="font-medium">{item.title}</span>}
                            </div>
                            <div className="absolute inset-0 bg-white/5 rounded-lg transform scale-0 group-hover:scale-100 transition-transform duration-200 ease-out" />
                          </NavLink>
                        )}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
      </Sidebar>

      {/* Dealership Panel (flag OFF) */}
      {!useDWProfiles && isPanelOpen && (
        <div className="fixed left-64 top-0 bottom-0 w-80 z-50 bg-background border-r border-border shadow-lg animate-slide-in-right"
             style={{ left: state === "collapsed" ? "4rem" : "16rem" }}>
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground">Your Dealerships</h2>
            <button onClick={() => setIsPanelOpen(false)} className="p-2 hover:bg-accent rounded-md transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-4">
            {dealershipContent}
          </div>
        </div>
      )}

      {/* Dealership Modal (flag ON) */}
      {useDWProfiles && (
        <Dialog open={isPanelOpen} onOpenChange={setIsPanelOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Your Dealerships</DialogTitle>
            </DialogHeader>
            {dealershipContent}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
